<script setup>
import { computed, defineEmits, defineProps, ref } from "vue";

const props = defineProps({
  formularioAberto: { type: Boolean, default: false },
  formulario: { type: Object, required: true },
  statusOpcoes: { type: Array, default: () => [] },
  clientes: { type: Array, default: () => [] },
  contatosCliente: { type: Array, default: () => [] },
  vendedores: { type: Array, default: () => [] },
  tiposDeEquipamento: { type: Array, default: () => [] },
  abrirNovoContato: { type: Boolean, default: false },
  novoContato: {
    type: Object,
    default: () => ({ nome: "", email: "", telefone: "", celular: "" })
  },
  followUps: { type: Array, default: () => [] },
  revisoes: { type: Array, default: () => [] },
  carregando: { type: Boolean, default: false },
  somenteLeitura: { type: Boolean, default: false }
});

const emits = defineEmits([
  "update:formularioAberto",
  "fechar",
  "salvar",
  "selecionar-cliente",
  "selecionar-contato",
  "selecionar-contato-tecnico",
  "selecionar-tipo-equipamento",
  "criar-contato",
  "update:abrirNovoContato",
  "novo-followup",
  "editar-followup",
  "deletar-followup",
  "gerar-job"
]);

const dialogModel = computed({
  get: () => props.formularioAberto,
  set: (valor) => emits("update:formularioAberto", valor)
});

const abrirNovoContatoModel = computed({
  get: () => props.abrirNovoContato,
  set: (valor) => emits("update:abrirNovoContato", valor)
});

const panelCliente = ref([0]);
const panelTecnica = ref([0]);
const panelComercial = ref([0]);
const panelFollowUp = ref([0]);
const panelRevisoes = ref([]);

const tipoContatoDialog = ref("comercial");

// eslint-disable-next-line no-unused-vars
const tipoEquipamentoItemText = (item) => [
  item?.sigla || item?.codigo || item?.code,
  item?.descricao || item?.nome || item?.description || item?.name
].filter(Boolean).join(" – ");
const tipoEquipamentoFilter = (item, queryText) => {
  const texto = `${item?.sigla || item?.codigo || item?.code || ""} ${item?.descricao || item?.nome || item?.description || item?.name || ""}`.toLowerCase();
  return texto.includes((queryText || "").toLowerCase());
};

const dialogFollowUp = ref(false);
const followUpItemEdicao = ref(null);
const followUpForm = ref({ description: "", data: "", contato_comercial: "", status: null });

function abrirNovoFollowUp() {
  followUpForm.value = {
    description: "",
    data: new Date().toISOString().split("T")[0],
    contato_comercial: "",
    status: null
  };
  followUpItemEdicao.value = null;
  dialogFollowUp.value = true;
}

function abrirEditarFollowUp(item) {
  followUpForm.value = {
    description: item.description || "",
    data: item.data || "",
    contato_comercial: item.contato_comercial || "",
    status: item.status || null
  };
  followUpItemEdicao.value = item;
  dialogFollowUp.value = true;
}

function confirmarFollowUp() {
  if (followUpItemEdicao.value) {
    emits("editar-followup", { ...followUpItemEdicao.value, ...followUpForm.value });
  } else {
    emits("novo-followup", { ...followUpForm.value });
  }
  dialogFollowUp.value = false;
}

const headersFollowUp = [
  { text: "Acoes", value: "acoes", sortable: false, width: "90px" },
  { text: "Codigo", value: "code", sortable: false },
  { text: "Descricao", value: "description", sortable: false },
  { text: "Data", value: "data", sortable: false },
  { text: "Contato Comercial", value: "contato_comercial", sortable: false },
  { text: "Status", value: "statusDescription", sortable: false }
];

const headersRevisoes = [
  { text: "Revisao", value: "revisao", sortable: false },
  { text: "Data Geracao", value: "dateGenerate", sortable: false },
  { text: "Status", value: "statusDescription", sortable: false },
  { text: "Tipo de Equipamento", value: "_mirror_tipoDeEquipamento", sortable: false }
];
</script>

<template>
  <div>
    <v-dialog v-model="dialogModel" fullscreen persistent transition="dialog-bottom-transition">
      <v-card tile class="so-modal">
        <div class="so-topbar">
          <v-btn color="error" dark :disabled="props.carregando" @click="emits('fechar')">
            Fechar
          </v-btn>

          <p class="so-topbar__title">
            <v-icon color="white" class="mr-2">mdi-file-document-edit-outline</v-icon>
            Solicitação de Orçamento
          </p>

          <div class="so-topbar__actions">
            <v-btn
              v-if="!props.somenteLeitura"
              color="primary"
              dark
              :loading="props.carregando"
              @click="emits('salvar')"
            >
              Gravar
            </v-btn>
            <v-btn
              v-if="!props.somenteLeitura"
              color="success"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="emits('gerar-job')"
            >
                Gerar Job
            </v-btn>
          </div>
        </div>

        <div class="so-body">
          <div class="so-form">
            <fieldset class="so-fieldset" :disabled="props.somenteLeitura">

            <!-- Cabecalho -->
            <v-row dense align="center" class="mb-0">
              <v-col cols="12" md="3" class="pb-0">
                <v-checkbox
                  v-model="props.formulario.contabilizar"
                  label="Contabilizar S.O"
                  dense hide-details class="mt-1"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Numero Proposta"
                  v-model="props.formulario.numeroProposta"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  :items="props.tiposDeEquipamento"
                  :item-text="tipoEquipamentoItemText"
                  :filter="tipoEquipamentoFilter"
                  item-value="id"
                  label="Tipo de Equipamento"
                  v-model="props.formulario.tipoDeEquipamento"
                  outlined dense hide-details clearable
                  @change="(v) => emits('selecionar-tipo-equipamento', v)"
                >
                  <template v-slot:item="{ item }">
                    <span><strong>{{ item.sigla || item.codigo || item.code }}</strong> – {{ item.descricao || item.nome || item.description || item.name }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span><strong>{{ item.sigla || item.codigo || item.code }}</strong> – {{ item.descricao || item.nome || item.description || item.name }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row dense class="mt-3">
              <v-col cols="12" md="3">
                <v-text-field label="Origem" v-model="props.formulario.origem" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field label="Ultima Revisao" v-model="props.formulario.ultimaRevisao" readonly outlined dense hide-details class="so-readonly" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Status"
                  :value="props.formulario.statusDescription || props.formulario.status || ''"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Job"
                  v-model="props.formulario.jobNome"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field label="Revisao" v-model="props.formulario.revisao" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Data Criacao"
                  type="date"
                  v-model="props.formulario.dataCriacao"
                  outlined dense hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Data Geracao"
                  type="date"
                  v-model="props.formulario.dateGenerate"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Tipo de Faturamento"
                  v-model="props.formulario.tipoFaturamento"
                  outlined dense hide-details
                />
              </v-col>
            </v-row>

            <!-- Descricao / Observacoes -->
            <v-row dense class="mt-3">
              <v-col cols="12" md="9">
                <v-textarea
                  label="Descrição do Projeto / Equipamento"
                  v-model="props.formulario.descricao"
                  outlined dense rows="2" auto-grow
                  :class="{ 'so-campo-obrigatorio': !props.formulario.descricao }"
                  hint="Obrigatório — descreva o equipamento ou serviço a ser orçado"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-textarea
                  label="Observações"
                  v-model="props.formulario.observacoes"
                  outlined dense hide-details rows="2" auto-grow
                />
              </v-col>
            </v-row>

            <!-- SECAO: Cliente -->
            <v-expansion-panels v-model="panelCliente" flat multiple class="mt-4 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Cliente</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-autocomplete
                          class="flex-grow-1"
                          :items="props.clientes"
                          item-text="nomeFantasia"
                          item-value="id"
                          label="Cliente"
                          v-model="props.formulario.cliente"
                          outlined dense hide-details clearable
                          @change="(v) => emits('selecionar-cliente', v)"
                        />
                        <v-btn icon small color="primary" :disabled="props.somenteLeitura"><v-icon>mdi-plus</v-icon></v-btn>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        label="Referencia do Cliente"
                        v-model="props.formulario.referenciaCliente"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-autocomplete
                          class="flex-grow-1"
                          :items="props.vendedores"
                          item-text="nome"
                          item-value="id"
                          label="Vendedor"
                          v-model="props.formulario.vendedor"
                          outlined dense hide-details clearable
                        />
                        <v-btn icon small color="primary" :disabled="props.somenteLeitura"><v-icon>mdi-plus</v-icon></v-btn>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-autocomplete
                          class="flex-grow-1"
                          :items="props.vendedores"
                          item-text="nome"
                          item-value="id"
                          label="Orcamentista"
                          v-model="props.formulario.orcamentista"
                          outlined dense hide-details clearable
                        />
                        <v-btn icon small color="primary"><v-icon>mdi-plus</v-icon></v-btn>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-autocomplete
                          class="flex-grow-1"
                          :items="props.contatosCliente"
                          item-text="nome"
                          item-value="id"
                          label="Contato Comercial"
                          v-model="props.formulario.contato"
                          outlined dense hide-details clearable
                          :disabled="!props.formulario.cliente"
                          @change="(v) => emits('selecionar-contato', v)"
                        />
                        <v-btn
                          icon small color="primary"
                          :disabled="!props.formulario.cliente || props.somenteLeitura"
                          @click="tipoContatoDialog = 'comercial'; abrirNovoContatoModel = true"
                        >
                          <v-icon>mdi-account-plus</v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial Telefone"
                        v-model="props.formulario.contatoComercialTelefone"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial Celular"
                        v-model="props.formulario.contatoComercialCelular"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial E-mail"
                        v-model="props.formulario.contatoComercialEmail"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <div class="d-flex align-center" style="gap:6px">
                        <v-autocomplete
                          class="flex-grow-1"
                          :items="props.contatosCliente"
                          item-text="nome"
                          item-value="id"
                          label="Contato Tecnico"
                          v-model="props.formulario.contatoTecnico"
                          outlined dense hide-details clearable
                          :disabled="!props.formulario.cliente"
                          @change="(v) => emits('selecionar-contato-tecnico', v)"
                        />
                        <v-btn
                          icon small color="primary"
                          :disabled="!props.formulario.cliente || props.somenteLeitura"
                          @click="tipoContatoDialog = 'tecnico'; abrirNovoContatoModel = true"
                        >
                          <v-icon>mdi-account-plus</v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico Nome"
                        v-model="props.formulario.contatoTecnicoNome"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico Telefone"
                        v-model="props.formulario.contatoTecnicoTelefone"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico Celular"
                        v-model="props.formulario.contatoTecnicoCelular"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico E-mail"
                        v-model="props.formulario.contatoTecnicoEmail"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Proposta Tecnica -->
            <v-expansion-panels v-model="panelTecnica" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Abertura de solicitacao de Orcamento Tecnica</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12" md="6">
                      <v-text-field
                        label="Projeto"
                        v-model="props.formulario.projeto"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Solicitacao"
                        type="date"
                        v-model="props.formulario.dataSolicitacao"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Despacho"
                        type="date"
                        v-model="props.formulario.dataDespacho"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="Data Conclusao"
                        type="date"
                        v-model="props.formulario.dataConclusao"
                        outlined dense hide-details
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Proposta Comercial -->
            <v-expansion-panels v-model="panelComercial" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Abertura de solicitacao de Orcamento Comercial</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Custo Disp. s/ICMS, PIS, COFINS c/IPI"
                        v-model="props.formulario.valorDisponivel"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="% PIS Custo"
                        v-model="props.formulario.porcentagemPIS"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="% COFINS Custo"
                        v-model="props.formulario.porcentagemCOFINS"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="KI do Custo"
                        v-model="props.formulario.ki"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="% IIS da Venda"
                        v-model="props.formulario.porcentagemIIS"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="% ICMS Venda"
                        v-model="props.formulario.porcentagemICMSVenda"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="% IPI"
                        v-model="props.formulario.porcentagemIPI"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="Preco de Venda c/ICMS s/IPI"
                        v-model="props.formulario.precoVenda_sIPI_cICMS"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="Preco de Venda s/ICMS s/IPI"
                        v-model="props.formulario.precoVenda_sIPI_sICMS"
                        readonly outlined dense hide-details
                        class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="Preco de Venda c/ISS"
                        v-model="props.formulario.precoVenda_cISS"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="KV"
                        v-model="props.formulario.kv"
                        readonly outlined dense hide-details
                        class="so-readonly"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Follow-Up -->
            <v-expansion-panels v-model="panelFollowUp" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Follow-Up</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex align-center mb-2" style="gap:8px">
                    <v-btn v-if="!props.somenteLeitura" icon small color="primary" @click="abrirNovoFollowUp()">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <v-text-field placeholder="Search" dense outlined hide-details style="max-width:220px" />
                  </div>
                  <v-data-table
                    :headers="headersFollowUp"
                    :items="props.followUps"
                    dense hide-default-footer class="elevation-0"
                    no-data-text="No data available"
                  >
                    <template v-slot:item.acoes="{ item }">
                      <div class="d-flex align-center">
                        <v-btn v-if="!props.somenteLeitura" icon x-small color="primary" @click="abrirEditarFollowUp(item)">
                          <v-icon x-small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn v-if="!props.somenteLeitura" icon x-small color="error" @click="emits('deletar-followup', item)">
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Revisoes -->
            <v-expansion-panels v-model="panelRevisoes" flat class="mt-2 so-panel mb-6">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Revisoes</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                    :headers="headersRevisoes"
                    :items="props.revisoes"
                    dense hide-default-footer class="elevation-0"
                    no-data-text="No data available"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            </fieldset>

        </div>
      </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Follow-Up -->
    <v-dialog v-model="dialogFollowUp" max-width="560px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-message-text-outline</v-icon>
          {{ followUpItemEdicao ? 'Editar Follow-Up' : 'Novo Follow-Up' }}
        </v-card-title>
        <v-card-text class="pt-5">
          <v-row dense>
            <v-col cols="12">
              <v-textarea
                label="Descricao"
                v-model="followUpForm.description"
                outlined dense rows="3" auto-grow hide-details
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-text-field
                label="Data"
                type="date"
                v-model="followUpForm.data"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-text-field
                label="Contato Comercial"
                v-model="followUpForm.contato_comercial"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" class="mt-3">
              <v-select
                :items="props.statusOpcoes"
                item-text="description"
                item-value="id"
                label="Status"
                v-model="followUpForm.status"
                outlined dense hide-details clearable
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="dialogFollowUp = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="props.carregando" @click="confirmarFollowUp()">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Novo Contato -->
    <v-dialog v-model="abrirNovoContatoModel" max-width="650px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-account-plus</v-icon>
          {{ tipoContatoDialog === 'tecnico' ? 'Novo contato técnico' : 'Novo contato comercial' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field label="Nome" v-model="props.novoContato.nome" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="E-mail" v-model="props.novoContato.email" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Telefone" v-model="props.novoContato.telefone" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Celular" v-model="props.novoContato.celular" outlined dense />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="abrirNovoContatoModel = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="props.carregando" @click="emits('criar-contato', { tipo: tipoContatoDialog })">
            Salvar contato
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.so-modal {
  background: #fafafa;
}

.so-topbar {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background-color: var(--v-primary-base);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.so-topbar__title {
  margin: 0;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.so-topbar__actions {
  display: flex;
  gap: 8px;
}

.so-body {
  height: calc(100vh - 60px);
  overflow: auto;
}

.so-form {
  padding: 20px 24px;
}

.so-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.so-panel {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.so-panel .so-panel__header {
  background: #f5f5f5;
  min-height: 40px !important;
}

.so-readonly .v-input__slot {
  background: #f5f5f5 !important;
}

.so-campo-obrigatorio .v-input__slot {
  border-color: #ff5252 !important;
  background: #fff8f8 !important;
}

.so-campo-obrigatorio fieldset {
  border-color: #ff5252 !important;
}
</style>
